import React, { useState, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';
import { Modal } from '../shared/Modal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import './UserManagement.css';
import { POSITIONS } from '../../constants/positions';

const NON_ADMIN_ROLES = ['Supervisor', 'Member'];

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // full list incl. Admin/Manager — for Team Lead candidates + name lookup
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [discardEditOpen, setDiscardEditOpen] = useState(false);
  const initialEditDataRef = useRef({});
  const [validationErrors, setValidationErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const [deactivateUser, setDeactivateUser] = useState(null);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);

  const TEAM_LEAD_ROLES = ['Supervisor', 'Manager', 'Admin'];
  const teamLeadCandidates = allUsers.filter(
    (u) => TEAM_LEAD_ROLES.includes(u.role) && u.id !== editingUser?.id
  );
  const userNameById = Object.fromEntries(allUsers.map((u) => [u.id, u.name]));

  const filteredUsers = useMemo(() => {
    let filtered = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(term) || u.name?.toLowerCase().includes(term)
      );
    }

    if (roleFilter) {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(u => u.status === statusFilter);
    }

    return filtered;
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      setError('');
      const fetched = await adminService.getAllUsers();
      if (isCancelled()) return;
      const nonAdminUsers = fetched.filter(u => !['Admin', 'Manager'].includes(u.role));
      setAllUsers(fetched);
      setUsers(nonAdminUsers);
    } catch (err) {
      if (isCancelled()) return;
      setError('Failed to load users');
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(fetchUsers, []);

  // Update one edit-form field and clear its validation error so the user
  // sees the red text go away once they start fixing the input.
  const updateEditField = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateEditForm = () => {
    const errors = {};

    if (!editFormData.name || editFormData.name.trim() === '') {
      errors.name = 'Name is required';
    }

    if (!editFormData.position || editFormData.position.trim() === '') {
      errors.position = 'Position is required';
    }

    if (!editFormData.role || editFormData.role.trim() === '') {
      errors.role = 'Role is required';
    }

    if (editFormData.email && !isValidEmail(editFormData.email)) {
      errors.email = 'Invalid email format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    const snapshot = {
      name: user.name || '',
      email: user.email || '',
      position: user.position || '',
      role: user.role || '',
      team_lead_id: user.team_lead_id || '',
    };
    setEditFormData(snapshot);
    initialEditDataRef.current = snapshot;
    setValidationErrors({});
    setEditModalOpen(true);
  };

  const isEditDirty = ['name', 'email', 'position', 'role', 'team_lead_id'].some(
    (k) => (editFormData[k] || '') !== (initialEditDataRef.current[k] || '')
  );

  const attemptCloseEdit = () => {
    if (isEditDirty && !actionLoading) setDiscardEditOpen(true);
    else setEditModalOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!validateEditForm()) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await adminService.updateUser(editingUser.id, editFormData);

      // Update local state
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...editFormData } : u));
      setEditModalOpen(false);
      setEditingUser(null);
      toast.success('User updated');
    } catch (err) {
      setError('Failed to update user: ' + mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateClick = (user) => {
    setDeactivateUser(user);
    setDeactivateModalOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    try {
      setActionLoading(true);
      setError('');
      await adminService.deactivateUser(deactivateUser.id);

      // Update local state
      setUsers(prev => prev.map(u => u.id === deactivateUser.id ? { ...u, status: 'inactive' } : u));
      const deactivatedEmail = deactivateUser.email;
      setDeactivateModalOpen(false);
      setDeactivateUser(null);
      toast.success(`Deactivated ${deactivatedEmail}`);
    } catch (err) {
      setError('Failed to deactivate user: ' + mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateClick = async (user) => {
    try {
      setActionLoading(true);
      setError('');
      await adminService.reactivateUser(user.id);

      // Update local state
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: 'active' } : u));
      toast.success(`Reactivated ${user.email}`);
    } catch (err) {
      setError('Failed to reactivate user: ' + mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="user-mgmt-loading">Loading users...</div>;

  return (
    <div className="user-management">
      {error && <div className="user-mgmt-error">{error}</div>}

      <div className="user-mgmt-controls">
        <input
          type="text"
          className="user-mgmt-search"
          placeholder="Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search users by email or name"
        />
        <select
          className="user-mgmt-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label="Filter by role"
        >
          <option value="">All Roles</option>
          <option value="Supervisor">Supervisor</option>
          <option value="Member">Member</option>
        </select>
        <select
          className="user-mgmt-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <table className="user-mgmt-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Position</th>
            <th>Role</th>
            <th>Team Lead</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length === 0 ? (
            <tr>
              <td colSpan="7" className="user-mgmt-no-data">No users found</td>
            </tr>
          ) : (
            filteredUsers.map(user => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.name}</td>
                <td>{user.position}</td>
                <td>
                  <span className={`badge badge-${user.role.toLowerCase().replace(/\s+/g, '-')}`}>
                    {user.role}
                  </span>
                </td>
                <td>{userNameById[user.team_lead_id] || '—'}</td>
                <td>
                  <span className={`badge badge-${user.status}`}>
                    {user.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="user-mgmt-actions">
                    <button
                      className="btn btn-edit"
                      onClick={() => handleEditClick(user)}
                      disabled={actionLoading}
                      aria-label={`Edit user ${user.email}`}
                    >
                      Edit
                    </button>
                    <button
                      className={`btn ${user.status === 'active' ? 'btn-deactivate' : 'btn-reactivate'}`}
                      onClick={() => user.status === 'active' ? handleDeactivateClick(user) : handleReactivateClick(user)}
                      disabled={actionLoading}
                      aria-label={`${user.status === 'active' ? 'Deactivate' : 'Reactivate'} user ${user.email}`}
                    >
                      {user.status === 'active' ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Edit User Modal */}
      <Modal isOpen={editModalOpen} onClose={attemptCloseEdit} role="dialog" aria-modal="true" aria-label="Edit user">
        <div className="user-mgmt-modal">
          <h2>Edit User</h2>

          <div className="form-group">
            <label htmlFor="user-edit-email">Email</label>
            <input id="user-edit-email" type="email" value={editFormData.email || ''} readOnly className="form-control-readonly" />
          </div>

          <div className="form-group">
            <label htmlFor="user-edit-name">Name</label>
            <input
              id="user-edit-name"
              type="text"
              value={editFormData.name || ''}
              onChange={(e) => updateEditField('name', e.target.value)}
              className={validationErrors.name ? 'form-control-error' : ''}
              aria-invalid={!!validationErrors.name}
              aria-describedby={validationErrors.name ? 'user-edit-name-error' : undefined}
              maxLength={100}
            />
            {validationErrors.name && <span id="user-edit-name-error" className="form-error">{validationErrors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="user-edit-position">Position</label>
            <select
              id="user-edit-position"
              value={editFormData.position || ''}
              onChange={(e) => updateEditField('position', e.target.value)}
              className={validationErrors.position ? 'form-control-error' : ''}
              aria-invalid={!!validationErrors.position}
              aria-describedby={validationErrors.position ? 'user-edit-position-error' : undefined}
            >
              {POSITIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {validationErrors.position && <span id="user-edit-position-error" className="form-error">{validationErrors.position}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="user-edit-role">Role</label>
            <select
              id="user-edit-role"
              value={editFormData.role || ''}
              onChange={(e) => updateEditField('role', e.target.value)}
              className={validationErrors.role ? 'form-control-error' : ''}
              aria-invalid={!!validationErrors.role}
              aria-describedby={validationErrors.role ? 'user-edit-role-error' : undefined}
            >
              <option value="">-- Select a role --</option>
              {NON_ADMIN_ROLES.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            {validationErrors.role && <span id="user-edit-role-error" className="form-error">{validationErrors.role}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="user-edit-team-lead">Team Lead</label>
            <select
              id="user-edit-team-lead"
              value={editFormData.team_lead_id || ''}
              onChange={(e) => updateEditField('team_lead_id', e.target.value)}
            >
              <option value="">— None —</option>
              {teamLeadCandidates.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
              ))}
            </select>
            <p className="field-help">
              The selected Team Lead is automatically added as one of this user's supervisors.
            </p>
          </div>

          <div className="user-mgmt-modal-actions">
            <button className="btn btn-secondary" onClick={attemptCloseEdit} disabled={actionLoading}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={actionLoading || Object.keys(validationErrors).length > 0}>{actionLoading ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal isOpen={deactivateModalOpen} onClose={() => setDeactivateModalOpen(false)} role="dialog" aria-modal="true" aria-label="Deactivate user confirmation">
        <div className="user-mgmt-modal">
          <h2>Deactivate User</h2>
          <p className="user-mgmt-modal-text">
            Are you sure you want to deactivate <strong>{deactivateUser?.email}</strong>? They will not be able to login.
          </p>
          <div className="user-mgmt-modal-actions">
            <button className="btn btn-secondary" onClick={() => setDeactivateModalOpen(false)} disabled={actionLoading}>Cancel</button>
            <button className="btn btn-deactivate" onClick={handleConfirmDeactivate} disabled={actionLoading}>{actionLoading ? 'Deactivating...' : 'Deactivate'}</button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={discardEditOpen}
        title="Discard unsaved changes?"
        message="Your edits to this user will be lost."
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={() => { setDiscardEditOpen(false); setEditModalOpen(false); }}
        onCancel={() => setDiscardEditOpen(false)}
      />
    </div>
  );
}
